import React, { useCallback, useEffect, useState } from "react";
import {
  tumBildirimGecmisiniGetir,
  tumBildirimGecmisiAlicilariniGetir,
} from "../services/notificationHistoryService";

const CATEGORY_OPTIONS = ["all", "admin", "system", "automation", "ai", "scheduled", "finance", "health", "calendar", "task", "family", "other"];

const LABELS = {
  tr: {
    title:"Bildirim Geçmişi", subtitle:"Sunucudan çıkan tüm bildirimler ve alıcı sonuçları",
    all:"Tümü", admin:"Admin", system:"Sistem", automation:"Otomatik", ai:"AI Asistan",
    scheduled:"Hatırlatma", finance:"Finans", health:"Sağlık", calendar:"Takvim", task:"Görevler", family:"Aile", other:"Diğer",
    period24:"24 saat", period7:"7 gün", period30:"30 gün", periodAll:"Tüm zamanlar",
    statusAll:"Tüm durumlar", sent:"Gönderildi", partial:"Kısmi", failed:"Hatalı", pending:"Bekliyor",
    channelAll:"Tüm kanallar", push:"Push", inApp:"Uygulama içi", activity:"Uygulama aktivitesi", search:"Başlık veya mesaj ara",
    refresh:"Yenile", loading:"Yükleniyor…", empty:"Filtreye uygun bildirim bulunamadı.",
    recipients:"Alıcı", errors:"Hata", more:"100 kayıt daha getir", moreRecipients:"Daha fazla alıcı",
    noRecipients:"Alıcı ayrıntısı bulunamadı.", recipientLoadFailed:"Alıcı ayrıntıları yüklenemedi. Yeniden denemek için Alıcı sayısına dokunun.", unknown:"Bilinmiyor",
  },
  en: {
    title:"Notification History", subtitle:"All server notifications and recipient results",
    all:"All", admin:"Admin", system:"System", automation:"Automation", ai:"AI Assistant",
    scheduled:"Reminders", finance:"Finance", health:"Health", calendar:"Calendar", task:"Tasks", family:"Family", other:"Other",
    period24:"24 hours", period7:"7 days", period30:"30 days", periodAll:"All time",
    statusAll:"All statuses", sent:"Sent", partial:"Partial", failed:"Failed", pending:"Pending",
    channelAll:"All channels", push:"Push", inApp:"In-app", activity:"App activity", search:"Search title or message",
    refresh:"Refresh", loading:"Loading…", empty:"No notification matches these filters.",
    recipients:"Recipients", errors:"Errors", more:"Load 100 more", moreRecipients:"More recipients",
    noRecipients:"Recipient details unavailable.", recipientLoadFailed:"Recipient details could not be loaded. Tap the recipient count to try again.", unknown:"Unknown",
  },
};

const dateText = (value, tr) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(tr ? "tr-TR" : "en-US", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" });
};

const channelLabels = (item, labels) => {
  const channels = Array.isArray(item.channels) && item.channels.length
    ? item.channels
    : String(item.channel || "").split("+").map((value) => value.trim()).filter(Boolean);
  return channels.map((value) => value === "in_app" ? labels.inApp : value === "push" ? labels.push : value === "activity" ? labels.activity : value).join(" + ") || labels.unknown;
};

const statusLabel = (value, labels) => {
  const normalized = String(value || "").toLowerCase();
  if (["sent", "completed", "delivered", "created"].includes(normalized)) return labels.sent;
  if (normalized === "partial") return labels.partial;
  if (normalized === "failed") return labels.failed;
  if (["pending", "queued", "bekliyor"].includes(normalized)) return labels.pending;
  return value || labels.unknown;
};

const moduleLabel = (item, tr) => {
  const labels = tr
    ? { finance:"Finans", health:"Sağlık", calendar:"Takvim", task:"Görev", family:"Aile", admin:"Yönetim", system:"Sistem", other:"Uygulama" }
    : { finance:"Finance", health:"Health", calendar:"Calendar", task:"Task", family:"Family", admin:"Management", system:"System", other:"Application" };
  return labels[item.category] || labels.other;
};

const displayContent = (item, tr) => {
  const activityPayload = item.activityPayload && typeof item.activityPayload === "object" ? item.activityPayload : {};
  const activityType = String(item.activityType || item.title || "").toLowerCase();
  const actor = String(item.activityActorName || "").trim() || (tr ? "Bir aile üyesi" : "A family member");
  const rawActivityItem = activityPayload.itemName || activityPayload.taskName || activityPayload.eventTitle || activityPayload.title || activityPayload.documentName || activityPayload.recordName || activityPayload.memberName || "";
  const legacyFinanceLabels = tr
    ? { "finance.savings":"Birikim", "finance.installments":"Taksitler", "finance.subscriptions":"Abonelikler", "finance.bills":"Faturalar" }
    : { "finance.savings":"Savings", "finance.installments":"Installments", "finance.subscriptions":"Subscriptions", "finance.bills":"Bills" };
  const activityItem = legacyFinanceLabels[rawActivityItem] || rawActivityItem;
  const activityAmount = activityPayload.amount !== undefined && activityPayload.amount !== null && activityPayload.amount !== ""
    ? String(activityPayload.amount)
    : "";

  if (activityType === "finance_added") {
    const financeType = String(activityPayload.financeType || activityPayload.kind || "").toLowerCase();
    const title = tr
      ? (financeType === "gelir" || financeType === "income" ? "Gelir kaydı eklendi" : financeType === "gider" || financeType === "expense" ? "Gider kaydı eklendi" : "Finans kaydı eklendi")
      : (financeType === "gelir" || financeType === "income" ? "Income record added" : financeType === "gider" || financeType === "expense" ? "Expense record added" : "Finance record added");
    const namedItem = activityItem ? `“${activityItem}” ` : "";
    const amountText = activityAmount ? ` · ${activityAmount}` : "";
    return {
      title,
      body: tr
        ? `${actor} ${namedItem}finans kaydını ekledi${amountText}.`
        : `${actor} added the finance record ${namedItem.trim()}${amountText}.`.replace(/\s+\./g, "."),
    };
  }

  const technicalMatch = String(item.title || "").match(/^(.*)_(added|updated|deleted|completed)$/i);
  if (!technicalMatch) return { title:item.title, body:item.body };
  const subject = moduleLabel(item, tr);
  const operation = technicalMatch[2].toLowerCase();
  const titleMap = tr
    ? { added:`${subject} kaydı eklendi`, updated:`${subject} kaydı güncellendi`, deleted:`${subject} kaydı silindi`, completed:`${subject} işlemi tamamlandı` }
    : { added:`${subject} record added`, updated:`${subject} record updated`, deleted:`${subject} record deleted`, completed:`${subject} action completed` };
  const bodyMap = tr
    ? { added:`${subject} bölümüne yeni bir kayıt eklendi.`, updated:`${subject} bölümündeki bir kayıt güncellendi.`, deleted:`${subject} bölümündeki bir kayıt silindi.`, completed:`${subject} bölümündeki işlem tamamlandı.` }
    : { added:`A new record was added to ${subject}.`, updated:`A record in ${subject} was updated.`, deleted:`A record in ${subject} was deleted.`, completed:`The action in ${subject} was completed.` };
  return { title:titleMap[operation] || item.title, body:bodyMap[operation] || item.body };
};

export default function NotificationHistoryAdmin({ tr = true }) {
  const l = LABELS[tr ? "tr" : "en"];
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("30d");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [channel, setChannel] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [recipients, setRecipients] = useState({});
  const [recipientErrors, setRecipientErrors] = useState({});
  const [recipientLoading, setRecipientLoading] = useState(null);

  const load = useCallback(async ({ append = false, next = null } = {}) => {
    try {
      setLoading(true);
      setError("");
      const result = await tumBildirimGecmisiniGetir({ cursor:next, period, category, status, channel, search, limit:100 });
      setItems((current) => append ? [...current, ...result.items] : result.items);
      setCursor(result.nextCursor);
      if (!append) {
        setOpenId(null);
        setRecipients({});
        setRecipientErrors({});
      }
    } catch (err) {
      setError(err?.message || (tr ? "Bildirim geçmişi yüklenemedi." : "Notification history could not be loaded."));
    } finally { setLoading(false); }
  }, [period, category, status, channel, search, tr]);

  useEffect(() => { load(); }, [load]);

  const toggleRecipients = async (item) => {
    if (openId === item.id) { setOpenId(null); return; }
    setOpenId(item.id);
    if (recipients[item.id]?.items?.length || recipients[item.id]?.total > 0) return;
    try {
      setRecipientLoading(item.id);
      setRecipientErrors((current) => ({ ...current, [item.id]:"" }));
      const result = await tumBildirimGecmisiAlicilariniGetir(item.id, 0, 100);
      setRecipients((current) => ({ ...current, [item.id]:result }));
    } catch (err) {
      setRecipientErrors((current) => ({ ...current, [item.id]:err?.message || l.recipientLoadFailed }));
      setOpenId(null);
    } finally { setRecipientLoading(null); }
  };

  const loadMoreRecipients = async (item) => {
    const current = recipients[item.id] || { items:[], total:0 };
    if (recipientLoading || current.items.length >= current.total) return;
    try {
      setRecipientLoading(item.id);
      const result = await tumBildirimGecmisiAlicilariniGetir(item.id, current.items.length, 100);
      setRecipients((all) => ({ ...all, [item.id]:{ items:[...current.items, ...result.items], total:result.total } }));
    } finally { setRecipientLoading(null); }
  };

  return (
    <div className="notificationHistoryCenter">
      <div className="notificationHistoryIntro"><div><strong>{l.title}</strong><p>{l.subtitle}</p></div><button type="button" className="darkSecondaryBtn mini" onClick={() => load()} disabled={loading}>{l.refresh}</button></div>
      <div className="notificationHistoryFilters">
        <select className="darkInput" value={period} onChange={(e) => setPeriod(e.target.value)}><option value="24h">{l.period24}</option><option value="7d">{l.period7}</option><option value="30d">{l.period30}</option><option value="all">{l.periodAll}</option></select>
        <select className="darkInput" value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORY_OPTIONS.map((key) => <option key={key} value={key}>{l[key]}</option>)}</select>
        <select className="darkInput" value={status} onChange={(e) => setStatus(e.target.value)}><option value="all">{l.statusAll}</option><option value="sent">{l.sent}</option><option value="partial">{l.partial}</option><option value="failed">{l.failed}</option><option value="pending">{l.pending}</option></select>
        <select className="darkInput" value={channel} onChange={(e) => setChannel(e.target.value)}><option value="all">{l.channelAll}</option><option value="push">{l.push}</option><option value="in_app">{l.inApp}</option></select>
        <form className="notificationHistorySearch" onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }}><input className="darkInput" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={l.search}/><button type="submit" className="darkSecondaryBtn mini">⌕</button></form>
      </div>
      {error && <div className="profilError">{error}</div>}
      <div className="notificationHistoryList">
        {!items.length && loading ? <div className="emptyState">{l.loading}</div> : !items.length ? <div className="emptyState">{l.empty}</div> : items.map((item) => {
          const detail = recipients[item.id] || { items:[], total:0 };
          const content = displayContent(item, tr);
          return <article key={item.id} className="notificationHistoryItem">
            <div className="notificationHistoryItemHead"><div><span className={`notificationHistoryCategory ${item.category}`}>{l[item.category] || l.other}</span><strong>{content.title || l.unknown}</strong></div><time>{dateText(item.createdAt, tr)}</time></div>
            {content.body && <p>{content.body}</p>}
            <div className="notificationHistoryMeta"><span className="notificationHistoryChannel">{channelLabels(item, l)}</span><span className={`notificationHistoryStatus ${item.status || ""}`}>{statusLabel(item.status, l)}</span><button type="button" disabled={Number(item.recipientCount || 0) < 1} onClick={() => toggleRecipients(item)}>{l.recipients}: <b>{item.recipientCount || 0}</b></button><span className={item.failedCount > 0 ? "danger" : ""}>{l.errors}: {item.failedCount || 0}</span></div>
            {recipientErrors[item.id] && <div className="profilError">{l.recipientLoadFailed}</div>}
            {openId === item.id && <div className="notificationHistoryRecipients">{recipientLoading === item.id && !detail.items.length ? <small>{l.loading}</small> : !detail.items.length ? <small>{l.noRecipients}</small> : detail.items.map((recipient) => <div key={recipient.id || recipient.email}><div><strong>{recipient.displayName || l.unknown}</strong><small>{recipient.platform || recipient.channel || ""}</small></div><time>{dateText(recipient.sentAt, tr)}</time></div>)}{detail.items.length < detail.total && <button type="button" className="darkSecondaryBtn mini" onClick={() => loadMoreRecipients(item)}>{l.moreRecipients}</button>}</div>}
          </article>;
        })}
      </div>
      {cursor && <button type="button" className="darkSecondaryBtn notificationHistoryMore" disabled={loading} onClick={() => load({ append:true, next:cursor })}>{loading ? l.loading : l.more}</button>}
    </div>
  );
}
